/**
 * PerformancePanelLive — 2026-06-06 j-perf-history.
 *
 * Real performance history graph for the connected Alpaca Paper account.
 * Calls GET /api/v1/user/portfolio/performance-history?range=...
 *
 * Rules enforced:
 *  - Never renders fake series. No defaultAccount, no mock, no Brain/
 *    Owl/Compass, no hardcoded AAPL/NVDA chart data.
 *  - When the backend reports fewer than 2 points OR the user has no
 *    connected broker, renders "Performance history starts after
 *    additional syncs." (or the equivalent not-connected copy).
 *  - Tooltip values use the canonical accountFormatters: $236,724.50 /
 *    +$50.00 / -$472.81 / shared date formatter.
 *  - Range buttons: 1D / 1W / 1M / 3M / YTD / ALL.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { apiV1Base } from '../lib/apiConfig';
import { subscribeRefresh } from '../lib/refreshEvents';
import {
  EM_DASH,
  formatUsd,
  formatPnL,
  formatPercent,
  formatDateTime,
} from '../lib/accountFormatters';

const RANGES = ['1D', '1W', '1M', '3M', 'YTD', 'ALL'] as const;
type Range = typeof RANGES[number];

interface PerformancePoint {
  timestamp: string;
  equity: string | null;
  portfolio_value: string | null;
  cash: string | null;
  buying_power: string | null;
  daily_pl: string | null;
  daily_pl_pct: string | null;
}

interface PerformanceHistory {
  range: Range;
  points: PerformancePoint[];
  point_count: number;
  distinct_count?: number;
  sync_status: 'synced' | 'sync_pending' | 'not_connected' | string;
  message: string | null;
  source: string;
}

function _safeNumber(s: string | null | undefined): number | null {
  if (s === null || s === undefined || s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function fetchHistory(range: Range, token: string | null): Promise<PerformanceHistory | { error: string }> {
  if (!token) return Promise.resolve({ error: 'unauthenticated' });
  return fetch(
    `${apiV1Base()}/user/portfolio/performance-history?range=${range}`,
    {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'omit',
    },
  )
    .then(async (r) => {
      if (!r.ok) return { error: `HTTP ${r.status}` };
      const body = await r.json();
      const data = body?.data ?? body;
      return data as PerformanceHistory;
    })
    .catch((e) => ({ error: String(e?.message || e) }));
}

function _renderTooltip({ active, payload }: any) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl p-3 shadow-lg text-xs">
      <div className="font-bold text-[var(--app-text)]">{formatDateTime(p.timestamp)}</div>
      <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1">
        <span className="text-[var(--muted-text)]">Equity</span>
        <span className="font-bold text-[var(--app-text)]">{formatUsd(p.equity)}</span>
        <span className="text-[var(--muted-text)]">Portfolio</span>
        <span className="font-bold text-[var(--app-text)]">{formatUsd(p.portfolio_value)}</span>
        <span className="text-[var(--muted-text)]">Cash</span>
        <span className="font-bold text-[var(--app-text)]">{formatUsd(p.cash)}</span>
        <span className="text-[var(--muted-text)]">Buying Power</span>
        <span className="font-bold text-[var(--app-text)]">{formatUsd(p.buying_power)}</span>
        <span className="text-[var(--muted-text)]">Day P&amp;L</span>
        <span className="font-bold text-[var(--app-text)]">{formatPnL(p.daily_pl)}</span>
        <span className="text-[var(--muted-text)]">Day P&amp;L %</span>
        <span className="font-bold text-[var(--app-text)]">{formatPercent(p.daily_pl_pct)}</span>
      </div>
    </div>
  );
}

export default function PerformancePanelLive() {
  const [range, setRange] = useState<Range>('1M');
  const [data, setData] = useState<PerformanceHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  // 2026-06-07 sitewide-refresh — bumped by adversyn:refresh:portfolio
  // event so Refresh Broker Data also reloads /performance-history.
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setErrored(false);
    const token = typeof localStorage !== 'undefined'
      ? localStorage.getItem('adversyn_auth_token') : null;
    fetchHistory(range, token).then((res) => {
      if (cancel) return;
      if (!res || (res as any).error) {
        setErrored(true);
        setData(null);
      } else {
        setData(res as PerformanceHistory);
      }
      setLoading(false);
    });
    return () => { cancel = true; };
  }, [range, reloadToken]);

  useEffect(() => {
    const off = subscribeRefresh('portfolio', () => setReloadToken((t) => t + 1));
    return () => { off(); };
  }, []);

  const series = useMemo(() => {
    if (!data || !Array.isArray(data.points)) return [] as any[];
    return data.points.map((p) => ({
      timestamp: p.timestamp,
      // Recharts wants numeric Y values.
      equity: _safeNumber(p.equity) ?? 0,
      portfolio_value: _safeNumber(p.portfolio_value) ?? 0,
      cash: _safeNumber(p.cash) ?? 0,
      buying_power: _safeNumber(p.buying_power) ?? 0,
      daily_pl: _safeNumber(p.daily_pl) ?? 0,
      daily_pl_pct: _safeNumber(p.daily_pl_pct) ?? 0,
    }));
  }, [data]);

  return (
    <div
      data-testid="portfolio-performance-panel"
      data-sync-status={String(data?.sync_status ?? 'unknown')}
      className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-black text-[var(--app-text)] uppercase italic tracking-tight">
          Performance History
        </h3>
        <div data-testid="perf-range-buttons" className="flex items-center gap-1">
          {RANGES.map((r) => (
            <button
              key={r}
              data-testid={`perf-range-${r}`}
              onClick={() => setRange(r)}
              className={
                'px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-widest border ' +
                (r === range
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-[var(--input-bg)] text-[var(--muted-text)] border-[var(--input-border)] hover:text-[var(--app-text)]')
              }
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div data-testid="perf-loading"
             className="h-40 flex items-center justify-center text-xs font-bold uppercase tracking-widest text-[var(--muted-text)]">
          Loading performance history…
        </div>
      )}

      {!loading && errored && (
        <div data-testid="perf-unavailable"
             className="h-40 flex items-center justify-center text-center text-xs font-bold uppercase tracking-widest text-[var(--muted-text)] px-4">
          Performance history unavailable. Try again after the next Alpaca Paper sync.
        </div>
      )}

      {!loading && !errored && data && data.point_count < 2 && (  /* n-perf-flat-chart */
        <div data-testid="perf-insufficient"
             className="h-40 flex items-center justify-center text-center text-xs font-bold uppercase tracking-widest text-[var(--muted-text)] px-4">
          {data.point_count === 0 ? (data.message || 'Connect Alpaca Paper to start collecting performance history.') : 'Performance history starts after another broker sync.'}
        </div>
      )}

      {!loading && !errored && data && data.point_count >= 2 && (
        <>
          {(data.sync_status === 'identical_values' || (data.distinct_count !== undefined && data.distinct_count < 2)) && (
            <div data-testid="perf-flat-banner" className="mb-3 px-3 py-2 rounded-md border border-amber-500/30 bg-amber-500/10 text-[10px] font-black uppercase tracking-widest text-amber-700">
              Equity is flat across collected snapshots. {data.message || 'Refresh broker data after market movement to update the curve.'}
            </div>
          )}
          <div data-testid="perf-chart" className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 10, right: 16, left: 16, bottom: 0 }}>
              <defs>
                <linearGradient id="perfEquityFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--card-border)" opacity={0.3} />
              <XAxis
                dataKey="timestamp"
                tickFormatter={(t) => formatDateTime(t).slice(5, 16)}
                tick={{ fontSize: 10 }}
                stroke="var(--muted-text)"
              />
              <YAxis
                tickFormatter={(v) => v === 0 ? EM_DASH : '$' + (v / 1000).toFixed(0) + 'k'}
                tick={{ fontSize: 10 }}
                stroke="var(--muted-text)"
                domain={['auto', 'auto']}
              />
              <Tooltip content={_renderTooltip} />
              <Area type="monotone" dataKey="equity" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#perfEquityFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        </>
      )}

      <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
        Source: /api/v1/user/portfolio/performance-history · range={range}{data ? ` · points=${data.point_count}` : ''}
      </div>
    </div>
  );
}
